import React, { useState } from 'react';
import {
  ChevronDown,
  ClipboardList,
  CheckCircle2,
  AlertCircle,
  Info,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import type { ResponseMetadata } from '../../services/threadService';

const DIMENSION_ORDER = [
  'factuality',
  'completeness',
  'safety',
  'clinical_usefulness',
  'format_contract',
] as const;
type DimensionKey = (typeof DIMENSION_ORDER)[number];
type OrderedScore = { key: string; value: number };

function dimLabel(key: string): string {
  const map: Record<string, string> = {
    factuality: 'Factuality',
    completeness: 'Completeness',
    safety: 'Safety',
    clinical_usefulness: 'Clinical usefulness',
    format_contract: 'Format contract',
  };
  return map[key] ?? key.replace(/_/g, ' ');
}

function scoreClass(score: number): string {
  if (score >= 4) return 'bg-status-positive-bg text-status-positive-text border-(--status-positive)/30';
  if (score === 3) return 'bg-status-attention-bg text-status-attention-text border-(--status-attention)/30';
  return 'bg-status-negative-bg text-status-negative-text border-(--status-negative)/30';
}

function parseThinkingFlow(
  raw: ResponseMetadata['clinical_thinking_flow'],
): Array<{ step: string; title: string; body: string }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;
      const step = String(o.step ?? '');
      const title = String(o.title ?? o.step_title ?? '');
      const body = String(o.body ?? o.step_body ?? '');
      if (!title && !body) return null;
      return { step: step || '?', title: title || `Step ${step}`, body };
    })
    .filter((x): x is { step: string; title: string; body: string } => x != null);
}

function skipReasonCode(meta: ResponseMetadata): string | undefined {
  return meta.clinical_quality_refinement_skipped_reason ?? meta.clinical_quality_refinement_skipped;
}

function hasClinicalQualityData(meta: ResponseMetadata): boolean {
  const gs = meta.clinical_quality_gate_status;
  if (gs === 'passed' || gs === 'provisional' || gs === 'failed' || gs === 'skipped') return true;
  if (skipReasonCode(meta)) return true;
  if (meta.clinical_confidence_score != null && Number.isFinite(meta.clinical_confidence_score)) return true;
  const flow = parseThinkingFlow(meta.clinical_thinking_flow);
  if (flow.length > 0) return true;
  const scores = meta.clinical_rubric_scores;
  if (scores && typeof scores === 'object' && Object.keys(scores).length > 0) return true;
  return meta.clinical_quality_iterations != null && meta.clinical_quality_iterations > 0;
}

export function clinicalQualityVisible(meta: ResponseMetadata | undefined): boolean {
  if (!meta) return false;
  return hasClinicalQualityData(meta);
}

type Props = {
  metadata: ResponseMetadata;
  /** Nearest preceding user message text (for re-run with quality check). */
  clinicalQualityRerunUserText?: string;
  onClinicalQualityRerun?: (userPrompt: string) => void;
};

type GateUi = 'passed' | 'provisional' | 'failed' | 'skipped' | 'unknown';

function gateStatus(meta: ResponseMetadata): GateUi {
  const s = meta.clinical_quality_gate_status;
  if (s === 'passed' || s === 'provisional' || s === 'failed' || s === 'skipped') {
    return s;
  }
  const skipped = skipReasonCode(meta);
  if (skipped) return 'skipped';
  const g = meta.clinical_quality_gate_satisfied;
  if (g === true) return 'passed';
  if (g === false) return 'provisional';
  return 'unknown';
}

/**
 * Surfaces backend clinical quality loop: rubric scores, thinking-flow steps, gate status.
 */
export function ClinicalQualityReviewPanel({
  metadata,
  clinicalQualityRerunUserText,
  onClinicalQualityRerun,
}: Props) {
  // #115: response-body collapsibles start closed by default.
  const [open, setOpen] = useState(false);
  if (!hasClinicalQualityData(metadata)) return null;

  const flow = parseThinkingFlow(metadata.clinical_thinking_flow);
  const scores = metadata.clinical_rubric_scores ?? {};
  const skipped = skipReasonCode(metadata);
  const iterations = metadata.clinical_quality_iterations;
  const stability = metadata.clinical_stability_warnings ?? [];
  // Residual risk comes only from the structured backend field — never from
  // sniffing LLM-generated step titles.
  const residual = metadata.clinical_residual_risk_summary?.trim();
  const status = gateStatus(metadata);
  const conf = metadata.clinical_confidence_score;
  const regression = metadata.clinical_regression_detected === true;
  const plateau = metadata.clinical_quality_plateau_detected === true;
  const evalUncertainty = stability.length > 0;

  const orderedScores: OrderedScore[] = DIMENSION_ORDER.filter((k) => scores[k] != null).map((k: DimensionKey) => ({
    key: k,
    value: Number(scores[k]),
  }));
  const extraKeys = Object.keys(scores).filter((k) => !DIMENSION_ORDER.includes(k as (typeof DIMENSION_ORDER)[number]));
  for (const k of extraKeys) {
    const v = scores[k];
    if (v != null) orderedScores.push({ key: k, value: Number(v) });
  }

  const allHigh =
    orderedScores.length >= 5 && orderedScores.every((s) => s.value >= 4);

  const canRerunQuality =
    skipped === 'streamed_synthesis' &&
    Boolean(clinicalQualityRerunUserText?.trim()) &&
    typeof onClinicalQualityRerun === 'function';

  return (
    <div className="rounded border border-border bg-surface-sunken/20 overflow-hidden">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-sunken/40 transition-colors">
          <div className="flex items-center gap-2 min-w-0">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ClipboardList className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">Clinical Quality Review</div>
              <div className="text-[11px] text-text-tertiary truncate">
                {status === 'skipped'
                  ? skippedLabel(skipped ?? '')
                  : status === 'passed' || allHigh
                    ? 'Clinical quality gate passed (rubric floors met).'
                    : status === 'provisional'
                      ? 'Provisional: some targets not met — verify in chart before clinical use.'
                      : status === 'failed'
                        ? 'Quality gate failed: hard safety/factuality floors not met — do not rely on this output for care without review.'
                        : 'Rubric scoring and refinement summary.'}
              </div>
            </div>
          </div>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4 border-t border-border/50 pt-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {status === 'passed' ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-(--status-positive)/35 bg-status-positive-bg px-2.5 py-1 text-status-positive-text">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  Clinical quality checked
                </span>
              ) : null}
              {status === 'provisional' ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-(--status-attention)/40 bg-status-attention-bg px-2.5 py-1 text-status-attention-text">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Provisional — verify in chart
                </span>
              ) : null}
              {status === 'failed' ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-(--status-negative)/35 bg-status-negative-bg px-2.5 py-1 text-status-negative-text">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  Gate failed
                </span>
              ) : null}
              {status === 'skipped' ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-(--status-attention)/40 bg-status-attention-bg px-2.5 py-1 text-status-attention-text">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  Skipped{skipped ? `: ${skipped}` : ''}
                </span>
              ) : null}
              {status === 'unknown' && !skipped ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-text-tertiary">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  Gate not reported
                </span>
              ) : null}
            </div>

            {evalUncertainty && !skipped ? (
              <div className="flex gap-2 rounded border border-(--status-attention)/35 bg-status-attention-bg/50 px-3 py-2 text-[11px] text-status-attention-text">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Evaluation uncertainty detected: rubric scores varied across passes; treat dimension scores as
                  approximate and prefer chart verification for critical decisions.
                </span>
              </div>
            ) : null}

            {conf != null && Number.isFinite(conf) ? (
              <div className="text-[11px] text-text-tertiary">
                <span className="font-medium text-foreground">Derived confidence (0–1): </span>
                <span className="tabular-nums">{conf.toFixed(3)}</span>
                <span className="text-text-tertiary"> — heuristic from rubric mean minus instability/skipped-loop penalties.</span>
              </div>
            ) : null}

            {(regression || plateau) && !skipped ? (
              <div className="flex flex-wrap gap-2 text-[10px]">
                {regression ? (
                  <span className="rounded-full border border-border bg-surface-sunken/40 px-2 py-0.5">
                    Regression protection applied
                  </span>
                ) : null}
                {plateau ? (
                  <span className="rounded-full border border-border bg-surface-sunken/40 px-2 py-0.5">
                    Plateau stop
                  </span>
                ) : null}
              </div>
            ) : null}

            {skipped ? (
              <div className="flex gap-2 rounded-lg border border-border/60 bg-surface-raised/80 px-3 py-2.5 text-xs text-text-tertiary">
                <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                <div className="space-y-2 min-w-0">
                  <p>{skippedDetail(skipped)}</p>
                  {canRerunQuality ? (
                    <button
                      type="button"
                      onClick={() => onClinicalQualityRerun!(clinicalQualityRerunUserText!.trim())}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-[11px] font-medium text-primary hover:bg-primary/15 transition-colors"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Run clinical quality verification
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {stability.length > 0 ? (
              <div className="rounded border border-(--status-attention)/35 bg-status-attention-bg/50 px-3 py-2.5">
                <div className="flex items-center gap-2 text-[11px] font-medium text-status-attention-text mb-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Scoring stability
                </div>
                <ul className="list-disc pl-4 text-[11px] text-text-tertiary space-y-0.5">
                  {stability.slice(0, 8).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {orderedScores.length > 0 ? (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary mb-2">
                  Rubric scores (1–5)
                </div>
                <div className="flex flex-wrap gap-2">
                  {orderedScores.map(({ key, value }) => (
                    <span
                      key={key}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium ${scoreClass(value)}`}
                    >
                      <span className="text-text-tertiary font-normal">{dimLabel(key)}</span>
                      <span className="tabular-nums">{value}/5</span>
                    </span>
                  ))}
                </div>
                {iterations != null && iterations > 0 ? (
                  <p className="text-[11px] text-text-tertiary mt-2">Scoring cycles: {iterations}</p>
                ) : null}
                {metadata.clinical_hard_constraints_met != null || metadata.clinical_soft_targets_met != null ? (
                  <p className="text-[11px] text-text-tertiary mt-1.5">
                    {metadata.clinical_hard_constraints_met != null ? (
                      <span>
                        Hard constraints: {metadata.clinical_hard_constraints_met ? 'met' : 'not met'}
                        {metadata.clinical_soft_targets_met != null ? ' · ' : ''}
                      </span>
                    ) : null}
                    {metadata.clinical_soft_targets_met != null ? (
                      <span>Soft targets: {metadata.clinical_soft_targets_met ? 'met' : 'not met'}</span>
                    ) : null}
                  </p>
                ) : null}
              </div>
            ) : null}

            {residual ? (
              <div className="rounded-lg border border-border/60 bg-surface-raised/50 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary mb-1">
                  Residual risk & confidence
                </div>
                <p className="text-[11px] text-text-tertiary whitespace-pre-wrap leading-relaxed">{residual}</p>
              </div>
            ) : null}

            {flow.length > 0 ? (
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary">Thinking flow</div>
                <ol className="space-y-3 list-none">
                  {flow.map((step, idx) => (
                    <li
                      key={`${step.step}-${idx}`}
                      className="rounded-lg border border-border/60 bg-surface-raised/50 px-3 py-2.5"
                    >
                      <div className="text-xs font-medium text-foreground mb-1">{step.title}</div>
                      <div className="text-[11px] text-text-tertiary whitespace-pre-wrap leading-relaxed">
                        {step.body}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function skippedLabel(code: string): string {
  switch (code) {
    case 'streamed_synthesis':
      return 'Automated quality loop skipped (streamed response).';
    case 'empty_response':
      return 'No response text to score.';
    case 'rubric_unavailable':
      return 'Rubric file not loaded; refinement disabled.';
    default:
      return 'Quality loop was not applied.';
  }
}

function skippedDetail(code: string): string {
  switch (code) {
    case 'streamed_synthesis':
      return (
        'The assistant streamed tokens during synthesis. Running the rubric repair loop afterward would ' +
        'desync what you already saw, so the server skipped it. Use “Re-run with clinical quality check” for a ' +
        'non-streaming pass with the full packaged review, set clinical_quality_no_stream in request options, or ' +
        'enable MARCH_CLINICAL_QUALITY_AFTER_STREAM on the server.'
      );
    case 'empty_response':
      return 'There was no assistant text to evaluate.';
    case 'rubric_unavailable':
      return 'The human rubric YAML could not be read; enable MARCH_CLINICAL_QUALITY_REFINEMENT only when the eval rubric is present.';
    default:
      return String(code);
  }
}
